/**
 * iter633 — Timed Events (Foodics-style day cards).
 */
import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarClock, Plus, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const DAYS = [
  { id: 'monday', short: 'Mon', color: 'bg-sky-100 text-sky-800' },
  { id: 'tuesday', short: 'Tue', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'wednesday', short: 'Wed', color: 'bg-amber-100 text-amber-800' },
  { id: 'thursday', short: 'Thu', color: 'bg-purple-100 text-purple-800' },
  { id: 'friday', short: 'Fri', color: 'bg-pink-100 text-pink-800' },
  { id: 'saturday', short: 'Sat', color: 'bg-orange-100 text-orange-800' },
  { id: 'sunday', short: 'Sun', color: 'bg-rose-100 text-rose-800' },
];

export default function TimedEventsPage() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ name: '', day: 'monday', start_time: '', end_time: '', date_from: '', date_to: '', recurrence: 'weekly' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const { data } = await api.get('/timed-events');
      setEvents(data.events || []);
    } catch { toast.error('Failed to load'); }
  };

  const add = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      await api.post('/timed-events', form);
      setForm({ name: '', day: 'monday', start_time: '', end_time: '', date_from: '', date_to: '', recurrence: 'weekly' });
      load();
      toast.success('Event added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggle = async (ev) => {
    try {
      await api.patch(`/timed-events/${ev.id}`, { status: ev.status === 'active' ? 'inactive' : 'active' });
      load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this event?')) return;
    try {
      await api.delete(`/timed-events/${id}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const byDay = (day) => events.filter(e => e.day === day);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4" data-testid="timed-events-page">
        <SettingsPageHeader
          icon={CalendarClock}
          title="Timed Events"
          subtitle="Schedule menu changes, promotions, or happy hours by day of the week."
          accent="indigo"
        />

        <Card data-testid="add-event-card">
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-semibold">Add a timed event</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-2">
              <Input placeholder="Event name (e.g., Happy Hour)" value={form.name}
                     onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                     className="lg:col-span-2"
                     data-testid="event-name-input" />
              <Select value={form.day} onValueChange={(v) => setForm(p => ({ ...p, day: v }))}>
                <SelectTrigger data-testid="event-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map(d => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.recurrence} onValueChange={(v) => setForm(p => ({ ...p, recurrence: v }))}>
                <SelectTrigger data-testid="event-recurrence-select" title="How often this event repeats. Evaluated at POS lookup time — no scheduler needed."><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Every week</SelectItem>
                  <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                  <SelectItem value="monthly_first">1st {DAYS.find(d => d.id === form.day)?.short || ''} of month</SelectItem>
                  <SelectItem value="monthly_last">Last {DAYS.find(d => d.id === form.day)?.short || ''} of month</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Input type="time" value={form.start_time} onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))} className="text-xs" data-testid="event-start-time-input" placeholder="From" title="Start time (HH:MM, 24h)" />
                <span className="text-[10px] text-muted-foreground">→</span>
                <Input type="time" value={form.end_time} onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))} className="text-xs" data-testid="event-end-time-input" placeholder="To" title="End time (HH:MM, 24h)" />
              </div>
              <Input type="date" value={form.date_from} onChange={(e) => setForm(p => ({ ...p, date_from: e.target.value }))} data-testid="event-from-input" title="Date range from (optional)" />
              <Input type="date" value={form.date_to} onChange={(e) => setForm(p => ({ ...p, date_to: e.target.value }))} data-testid="event-to-input" title="Date range to (optional)" />
            </div>
            <p className="text-[10px] text-muted-foreground">Time window is optional — leave blank for an all-day event. Recurrence controls how the POS evaluates whether the event is active on a given date.</p>
            <div className="flex justify-end">
              <Button onClick={add} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="event-add-btn">
                <Plus size={14} className="mr-1" /> Add Event
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="day-cards-grid">
          {DAYS.map(d => (
            <Card key={d.id} className="overflow-hidden" data-testid={`day-card-${d.id}`}>
              <div className={`${d.color} p-2 font-bold text-center text-sm`}>{d.short}</div>
              <CardContent className="p-2 space-y-1">
                {byDay(d.id).length === 0 ? (
                  <div className="text-[10px] text-muted-foreground italic text-center py-2">No events</div>
                ) : byDay(d.id).map(ev => (
                  <div key={ev.id} className="p-2 rounded border border-stone-200 bg-white text-[11px] space-y-1" data-testid={`event-${ev.id}`}>
                    <div className="font-semibold truncate">{ev.name}</div>
                    {ev.recurrence && ev.recurrence !== 'weekly' && (
                      <div className="text-[9px] text-purple-700 font-medium uppercase tracking-wide">
                        {ev.recurrence === 'biweekly' && '↻ Every 2 weeks'}
                        {ev.recurrence === 'monthly_first' && '↻ 1st of month'}
                        {ev.recurrence === 'monthly_last' && '↻ Last of month'}
                      </div>
                    )}
                    {(ev.start_time || ev.end_time) && (
                      <div className="text-[9px] text-emerald-700 font-mono">
                        ⏱ {ev.start_time || '00:00'}–{ev.end_time || '23:59'}
                      </div>
                    )}
                    {(ev.date_from || ev.date_to) && (
                      <div className="text-[9px] text-muted-foreground">
                        {ev.date_from || '—'} → {ev.date_to || '—'}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-1">
                      <Switch checked={ev.status === 'active'} onCheckedChange={() => toggle(ev)} className="scale-75" />
                      <button onClick={() => remove(ev.id)} className="text-rose-500 hover:text-rose-700" data-testid={`event-delete-${ev.id}`}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
